import { Component, inject, input, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { catchError, finalize, map, of } from 'rxjs';
import { FuseAlertComponent } from '../../../../../../../@fuse/components/alert';
import { FuseLoadingService } from '../../../../../../../@fuse/services/loading';
import { Directory } from '../../../../../../shared/models/directory';
import { PromptImportExportService } from '../../../../../../shared/services/prompt-import-export.service';

interface ImportSummary {
    fileName: string;
    directoryName: string;
    importedCount: number;
    failedCount: number;
    errors?: Array<{ index: number; name: string; message: string }>;
}

@Component({
    selector: 'app-import-prompts',
    templateUrl: './import-prompts.component.html',
    imports: [
        MatExpansionModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatIconModule,
        FuseAlertComponent,
    ],
})
export class ImportPromptsComponent {
    private readonly fuseLoadingService = inject(FuseLoadingService);
    private readonly promptImportExportService = inject(PromptImportExportService);
    private readonly fb = inject(FormBuilder);

    protected readonly showAlert = signal(false);
    protected readonly alertType = signal<'success' | 'error'>('success');
    protected readonly alertMessage = signal('');
    protected readonly importSummary = signal<ImportSummary | null>(null);

    public readonly importCompleted = signal(false);
    public readonly isLoading = signal(false);
    public readonly selectedFile = signal<File | null>(null);
    public readonly directories = input<Directory[]>([]);

    public importForm: FormGroup;

    constructor() {
        this.importForm = this.fb.group({
            directoryUid: ['', Validators.required],
            file: [null, Validators.required],
        });
    }

    protected onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const maxFileSize = 5 * 1024 * 1024;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];

            if (file.type !== 'application/json' && !file.name.toLowerCase().endsWith('.json')) {
                this.showError('Bitte wähle eine JSON-Datei aus.');
                this.importForm.get('file')?.setValue(null);
                this.selectedFile.set(null);
                return;
            }

            if (file.size > maxFileSize) {
                this.showError('Die Datei ist zu groß. Bitte wähle eine Datei kleiner als 5MB aus.');
                this.importForm.get('file')?.setValue(null);
                this.selectedFile.set(null);
                return;
            }
            this.selectedFile.set(file);
            this.importForm.get('file')?.setValue(file);
            this.showAlert.set(false);
        }
    }

    public onSubmit() {
        const file = this.selectedFile();
        if (this.importForm.invalid || !file) {
            return;
        }
        this.fuseLoadingService.show();
        this.isLoading.set(true);

        const directoryUid = this.importForm.get('directoryUid')?.value;
        const selectedFileName = file.name;

        const directoryName =
            this.directories().find((directory) => directory.uid === directoryUid)?.name ?? 'Unbekannt';

        this.promptImportExportService
            .importPrompts(directoryUid, file)
            .pipe(
                map((response) => ({ response, error: null, directoryName })),
                catchError((error) => of({ response: null, error, directoryName })),
                finalize(() => {
                    this.fuseLoadingService.hide();
                    this.isLoading.set(false);
                }),
            )
            .subscribe(({ response, error, directoryName }) => {
                this.importCompleted.set(true);
                if (error) {
                    const errorMessage = error.message || 'Unbekannter Fehler';
                    const validationErrors = error.details?.errors;

                    if (validationErrors && Array.isArray(validationErrors)) {
                        this.showError(`Validierungsfehler:\n${validationErrors.join('\n')}`);
                    } else {
                        this.showError(`Import fehlgeschlagen: ${errorMessage}`);
                    }
                    return;
                }

                this.importSummary.set({
                    fileName: selectedFileName,
                    directoryName,
                    importedCount: response.importedCount,
                    failedCount: response.failedCount,
                    errors: response.errors,
                });
                if (response.success) {
                    this.showSuccess(
                        `${response.importedCount} Prompt${response.importedCount === 1 ? '' : 's'} wurde${response.importedCount === 1 ? '' : 'n'} erfolgreich importiert.`,
                    );
                } else {
                    const errorDetails =
                        response.errors
                            ?.map(
                                (error: { index: number; name: string; message: string }) =>
                                    `Prompt ${error.index + 1} (${error.name}): ${error.message}`,
                            )
                            .join('\n') || '';
                    this.showError(
                        `${response.importedCount} von ${response.importedCount + response.failedCount} Prompts erfolgreich importiert.\n${errorDetails}`,
                    );
                }
            });
    }

    private showSuccess(message: string) {
        this.alertType.set('success');
        this.alertMessage.set(message);
        this.showAlert.set(true);
    }

    private showError(message: string) {
        this.alertType.set('error');
        this.alertMessage.set(message);
        this.showAlert.set(true);
    }
}
